import { cn } from "@/lib/utils";
import { dualCtaLandingText } from "@/lib/dualCtaLandingStrings";
import { Shield, Sparkles, Zap } from "lucide-react";

type Lang = "es" | "en";

export type MembershipFunnelConversionChromeProps = {
  labelLang: string;
  uiLang: Lang;
  brandName: string;
  variantDisplayName?: string | null;
  priceUsd?: number | null;
  discountRate?: number;
  paymentGateway?: "stripe" | "mercadopago" | null;
};

function fmtPriceUsd(amount: number, lang: Lang): string {
  try {
    return new Intl.NumberFormat(lang === "es" ? "es-AR" : "en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `USD $${amount.toFixed(2)}`;
  }
}

function benefitKeys(): Array<"benefitExclusive" | "benefitInstant" | "benefitFlexible"> {
  return ["benefitExclusive", "benefitInstant", "benefitFlexible"];
}

/** Pills compactas sobre el CTA en mobile — no tapa el centro del video. */
export function MembershipFunnelMobileTrustStrip({
  labelLang,
  uiLang,
}: Pick<MembershipFunnelConversionChromeProps, "labelLang" | "uiLang">) {
  const t = (key: Parameters<typeof dualCtaLandingText>[0]) =>
    dualCtaLandingText(key, labelLang);

  const chips = [
    { icon: Sparkles, text: t("trustChipExclusive") },
    { icon: Zap, text: t("trustChipInstant") },
    { icon: Shield, text: t("trustChipSecure") },
  ];

  return (
    <div
      className="flex flex-wrap items-center justify-center gap-1.5 mb-1"
      data-testid="membership-funnel-mobile-trust"
    >
      {chips.map(({ icon: Icon, text }) => (
        <span
          key={text}
          className="inline-flex items-center gap-1 rounded-full bg-white/[0.08] px-2.5 py-1 text-[10px] font-medium text-white/80 ring-1 ring-white/12"
        >
          <Icon className="h-3 w-3 shrink-0 text-amber-300/90" aria-hidden />
          {text}
        </span>
      ))}
      <span className="sr-only">{uiLang === "es" ? "Beneficios" : "Benefits"}</span>
    </div>
  );
}

/** Panel lateral desktop — copy + bullets; los CTAs viven en MembershipVideoCtaPanel stacked. */
export function MembershipFunnelDesktopAside({
  labelLang,
  uiLang,
  brandName,
  variantDisplayName,
  priceUsd,
  discountRate = 0,
  paymentGateway,
}: MembershipFunnelConversionChromeProps) {
  const t = (key: Parameters<typeof dualCtaLandingText>[0]) =>
    dualCtaLandingText(key, labelLang);

  const basePrice =
    typeof priceUsd === "number" && priceUsd > 0 ? priceUsd : null;
  const effectivePrice =
    basePrice != null && discountRate > 0
      ? Math.max(0, basePrice * (1 - discountRate))
      : basePrice;

  const gatewayLabel =
    paymentGateway === "mercadopago"
      ? "Mercado Pago"
      : paymentGateway === "stripe"
        ? "Stripe"
        : null;

  return (
    <aside
      className="hidden md:flex md:flex-col md:justify-center md:gap-5 md:px-8 md:py-10 md:w-[min(400px,36vw)] md:max-w-md md:shrink-0 md:text-left"
      data-testid="membership-funnel-desktop-aside"
      aria-label={t("funnelHeadline")}
    >
      <div className="space-y-2">
        {brandName ? (
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-400/80">
            {brandName}
          </p>
        ) : null}
        <h1 className="text-2xl font-bold tracking-tight text-white lg:text-[1.65rem] lg:leading-tight">
          {t("funnelHeadline")}
        </h1>
        <p className="text-sm leading-relaxed text-zinc-400">
          {t("funnelSubheadline")}
        </p>
        {variantDisplayName ? (
          <p className="text-xs text-zinc-500">{variantDisplayName}</p>
        ) : null}
      </div>

      <ul className="space-y-2.5" role="list">
        {benefitKeys().map((key) => (
          <li
            key={key}
            className="flex items-start gap-2.5 text-sm text-zinc-300"
          >
            <span
              className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-400/15 text-amber-300"
              aria-hidden
            >
              ✓
            </span>
            <span>{t(key)}</span>
          </li>
        ))}
      </ul>

      {effectivePrice != null ? (
        <div className="flex flex-wrap items-baseline gap-2">
          {basePrice != null && discountRate > 0 && effectivePrice < basePrice ? (
            <span className="text-sm text-zinc-500 line-through">
              {fmtPriceUsd(basePrice, uiLang)}
            </span>
          ) : null}
          <span className="text-xl font-bold text-white">
            {fmtPriceUsd(effectivePrice, uiLang)}
          </span>
          <span className="text-xs text-zinc-500">{t("pricePerMonth")}</span>
        </div>
      ) : (
        <p className="text-sm text-zinc-500">{t("vipSubtitle")}</p>
      )}

      <p className="flex items-center gap-2 text-xs text-zinc-500">
        <Shield className="h-3.5 w-3.5 text-emerald-400/80" aria-hidden />
        {gatewayLabel
          ? `${t("securePaymentWith")} ${gatewayLabel}`
          : t("trustLine")}
      </p>
    </aside>
  );
}